import { useCallback, useEffect, useRef, useState } from "react";
import {
  getGridColumnConfig,
  saveGridColumnConfig,
} from "@/util/gridColumnPreferences";
import useDialog from "@/hooks/useDialog";

// Mirrors the legacy ASP msgbox shown on entering Config mode (see
// PoliceCentralV2\ContainerCommon.vbs, Sub ConfigListView):
// "Drag column headers to re-arrange. Drag column edges to re-size.
//  Click the 'Save Config' button when done and confirm your changes when done."
const CONFIG_INSTRUCTIONS_CONTENT = (
  <>
    Drag column headers to re-arrange.
    <br />
    Drag column edges to re-size.
    <br />
    <br />
    Click the &apos;Save Config&apos; button when done and confirm your
    changes when done.
  </>
);

/**
 * Reconciles a saved column layout against the screen's current default
 * headers: columns no longer present (removed/renamed since the user last
 * saved) are dropped, columns not present in the saved layout (added since
 * the user last saved) are appended at the end in their default order.
 */
function reconcileHeaders(defaultHeaders, savedColumns) {
  if (!Array.isArray(savedColumns) || !savedColumns.length) {
    return defaultHeaders;
  }

  const byKey = new Map(defaultHeaders.map((header) => [header.key, header]));
  const ordered = [];

  savedColumns.forEach(({ key, width }) => {
    const header = byKey.get(key);
    if (header) {
      ordered.push(width ? { ...header, width } : header);
      byKey.delete(key);
    }
  });

  defaultHeaders.forEach((header) => {
    if (byKey.has(header.key)) ordered.push(header);
  });

  return ordered;
}

/**
 * Drives the in-place "Config" toggle (reorder + resize columns, then
 * save/discard) for a grid, mirroring the legacy ASP ConfigListView
 * behavior. See docs/plans/grid-column-config-feature.md for the full
 * design.
 *
 * @param {string} gridId - stable id for this grid, e.g. "Bookings:MiscNumbers".
 *   Pass a falsy value to make this hook a no-op passthrough (useful if a
 *   screen isn't ready to have a stable id yet).
 * @param {Array<{key: string, text: string}>} defaultHeaders
 */
export default function useGridColumnConfig(gridId, defaultHeaders) {
  const { showDialog } = useDialog();
  const [headers, setHeaders] = useState(defaultHeaders);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const dataTableRef = useRef(null);
  const lastSavedHeadersRef = useRef(defaultHeaders);

  useEffect(() => {
    let cancelled = false;

    if (!gridId) {
      setHeaders(defaultHeaders);
      lastSavedHeadersRef.current = defaultHeaders;
      return undefined;
    }

    getGridColumnConfig(gridId, defaultHeaders).then((saved) => {
      if (cancelled) return;
      const reconciled = reconcileHeaders(defaultHeaders, saved);
      setHeaders(reconciled);
      lastSavedHeadersRef.current = reconciled;
    });

    return () => {
      cancelled = true;
    };
    // Only re-run when the grid identity changes — defaultHeaders is
    // expected to be referentially stable-ish per screen; if it does
    // change (e.g. dynamic columns), the reconciliation on the next
    // gridId change (or the appended-at-end fallback in reconcileHeaders)
    // is enough, without racing a saved config in against every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridId]);

  const startConfiguring = useCallback(() => {
    if (!gridId) return;
    showDialog("info", {
      title: "Configure Columns",
      content: CONFIG_INSTRUCTIONS_CONTENT,
      confirmButtonText: "OK",
    });
    setIsConfiguring(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridId]);

  const confirmSave = useCallback(() => {
    const layout = dataTableRef.current?.getColumnLayout?.() ?? null;
    setIsConfiguring(false);
    if (!layout) return;

    saveGridColumnConfig(gridId, layout, defaultHeaders);

    const reconciled = reconcileHeaders(defaultHeaders, layout);
    setHeaders(reconciled);
    lastSavedHeadersRef.current = reconciled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridId]);

  const discardChanges = useCallback(() => {
    dataTableRef.current?.resetColumnLayout?.();
    setIsConfiguring(false);
    setHeaders(lastSavedHeadersRef.current);
  }, []);

  return {
    headers,
    isConfiguring,
    configButtonLabel: isConfiguring ? "Save Config" : "Config",
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  };
}
